<template>
  <div class="LmAdvancedSearch leading-zero">
    <lm-icon-button class="text-right" icon="search" @click.prevent="open" />
    <transition name="search-transition">
      <div v-show="show || forceOpen" class="SearchContainer">
        <input
          ref="SearchInput"
          class="LmSearchInput flex-grow mr-3 text-gray-700 text-s leading-normal focus:outline-none font-sans"
          :value="search"
          :placeholder="placeholder"
          @input="inputSearch"
        />
        <lm-loading v-if="loading" class="mr-3" :size="20" blue />
        <lm-icon-button
          v-else-if="tags || users"
          class="mr-3"
          :class="{'text-highlight': showHelp}"
          icon="help_outline"
          @click="toggleHelp"
        />
        <div class="border-l h-10 border-gray-300 pr-2" />
        <lm-icon-button icon="close" svg @click="close" />
      </div>
    </transition>

    <transition name="help-transition">
      <div v-if="showSuggestedTags || showHelp" class="HelpContainer">
        <div v-show="showSuggestedTags">
          <ul class="text-xs text-gray-700 leading-loose">
            <li
              v-for="(t, i) in suggestedTags"
              :key="t.slug"
              class="p-1 pl-3 cursor-pointer hover:bg-highlightBaseLight font-sans text-s"
              :class="{
                'bg-highlightBaseLight': tagSuggestionIndexSelected === i
              }"
              @click="selectTag(t)"
            >
              <lm-icon icon="tag" size="18" class="mr-2" />
              <span v-html="labelMatch(t.label)" />
              <span
                v-if="scopeTopics || scopeSources"
                class="ml-2 text-gray-500 text-xs font-sans"
                >({{ scopeTopics ? t.nTopics : t.nSources }})</span
              >
            </li>
          </ul>
        </div>
        <div v-show="showHelp">
          <h1 class="p-3 text-xxs uppercase bg-gray-300">
            Search examples
          </h1>
          <ul class="p-2 pl-3 text-xs text-gray-700 leading-loose">
            <li>some text</li>
            <template v-if="tags">
              <li>tag:innovation, ideas</li>
              <li>
                tag:"ideas"
                <span class="font-sans italic text-gray-500">
                  (exact tag match)
                </span>
              </li>
            </template>
            <template v-if="users">
              <li>user:Mike White</li>
              <li>user:Mike White; some text</li>
            </template>
            <template v-if="tags && users">
              <li>user:Mike White; tag:ideas</li>
              <li>some text; user:Mike White; tag:ideas</li>
            </template>
          </ul>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import LmIcon from '@/components/ui/LmIcon'
import {debounce} from 'lodash'
import {mapGetters} from 'vuex'
import List from '@/lib/list'
import LmIconButton from '@/components/ui/LmIconButton'
import LmLoading from '@/components/ui/LmLoading'

export default {
  name: 'LmAdvancedSearch',
  components: {LmIcon, LmIconButton, LmLoading},
  props: {
    placeholder: {
      type: String,
      default: 'Search'
    },
    forceOpen: {
      type: Boolean,
      default: false
    },
    tags: {
      type: Boolean,
      default: false
    },
    users: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    async: {
      type: Boolean,
      default: false
    },
    scopeTopics: {
      type: Boolean,
      default: false
    },
    scopeSources: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      search: '',
      show: false,
      showHelp: false,
      suggestedTags: [],
      tagSuggestActive: false,
      tagSuggestTimeout: false,
      tagSuggestionIndexSelected: -1,
      currentTagMatch: ''
    }
  },
  computed: {
    ...mapGetters(['currentLandscape']),
    showSuggestedTags() {
      return this.tagSuggestActive && this.suggestedTags.length > 0
    }
  },
  watch: {
    showSuggestedTags(v) {
      if (v) {
        this.tagSuggestionIndexSelected = -1
        document.addEventListener('keyup', this.onKeyup)
      } else {
        document.removeEventListener('keyup', this.onKeyup)
      }
    },
    $route() {
      this.setSearch(this.$route.query.search)
      if (!this.tagSuggestTimeout) {
        // TODO: refactor me!
        const activeSearch = this.search
          .split(';')
          .pop()
          .trim()
        if (activeSearch && activeSearch.slice(0, 4) === 'tag:') {
          this.tagSuggestActive = true
          const tagSearch = activeSearch.split('tag:').pop()
          const match = tagSearch.match('[^,]+$')
          if (match) {
            const activeSearchTag = match[0]?.trim()
            if (activeSearchTag) {
              this.currentTagMatch = activeSearchTag
              this.suggestedTags = this.currentLandscape.tags
                .filter(tag => List.tagMatch(activeSearchTag, tag))
                .filter(tag =>
                  this.scopeTopics
                    ? tag.nTopics > 0
                    : this.scopeSources
                    ? tag.nSources > 0
                    : false
                )
              return
            }
          }
        }
        this.tagSuggestActive = false
      }
    }
  },
  mounted() {
    this.setSearch(this.$route.query.search)
  },
  methods: {
    onKeyup(e) {
      switch (e.key) {
        case 'ArrowDown':
          if (this.tagSuggestionIndexSelected < this.suggestedTags.length - 1) {
            this.tagSuggestionIndexSelected++
          }
          if (this.tagSuggestionIndexSelected === 0) this.blur()
          break
        case 'ArrowUp':
          if (this.tagSuggestionIndexSelected >= 0)
            this.tagSuggestionIndexSelected--

          if (this.tagSuggestionIndexSelected === -1) this.focus()
          break
        case 'Enter':
          this.selectTag(this.suggestedTags[this.tagSuggestionIndexSelected])
      }
    },
    labelMatch(label) {
      const newLabel = label.replace(
        new RegExp(this.currentTagMatch, 'gi'),
        match => `<mark>${match}</mark>`
      )
      return newLabel
    },
    selectTag(t) {
      if (!t) return // prevent edge cases
      // TODO: refactor me!
      const activeSearch = this.search.split(';')
      const tags = activeSearch.pop().trim()
      if (tags && tags.slice(0, 4) === 'tag:') {
        const tagSearch = tags
          .split('tag:')
          .pop()
          .trim()
        const lastTagMatch = tagSearch.match('[^,]+$')
        if (lastTagMatch) {
          const lastTag = lastTagMatch[0].trim()
          if (lastTag) {
            const before =
              activeSearch.length > 0 ? activeSearch.join(';') + '; ' : ''
            const index = lastTagMatch.index
            const newTagSearch =
              tagSearch.substring(0, index) +
              `${index > 0 ? ' ' : ''}` +
              t.label
            this.setSearch(`${before}tag: ${newTagSearch}`)
            this.inputSearch({target: {value: this.search}})
            this.tagSuggestActive = false
            this.tagSuggestTimeout = true
            setTimeout(() => (this.tagSuggestTimeout = false), 500)
            this.focus()
          }
        }
      }
    },
    open() {
      this.show = true
      this.focus()
    },
    close() {
      this.search = ''
      this.show = false
      this.showHelp = false
      this.$router.removeKeyFromQuery('search')
    },
    setSearch(value = '') {
      this.search = value
    },
    inputSearch(event) {
      if (this.async) {
        this.doRouterPushDebounced(event)
      } else {
        this.routerPush(event)
      }
    },
    doRouterPushDebounced: debounce(
      function(event) {
        this.$router.pushQuery({search: event.target.value})
      },
      500,
      {leading: false}
    ),
    routerPush(event) {
      this.$router.pushQuery({search: event.target.value})
    },
    toggleHelp() {
      this.showHelp = !this.showHelp
      this.focus()
    },
    focus() {
      this.$nextTick(() => {
        this.$refs.SearchInput.focus()
      })
    },
    blur() {
      this.$nextTick(() => {
        this.$refs.SearchInput.blur()
      })
    }
  }
}
</script>

<style scoped>
.SearchContainer {
  transform-origin: 80%;
  @apply absolute left-0 right-0 z-1 h-10 flex items-center -mt-8 mx-1 px-2 bg-white border;
}
.HelpContainer {
  @apply absolute right-0 z-1 bg-white border font-mono;
  margin-top: 9px;
  margin-right: 4px;
}
@media (min-width: 640px) {
  .LmAdvancedSearch {
    position: relative;
  }
  .SearchContainer {
    transform-origin: 90%;
    left: auto;
    width: 320px;
    margin-right: -7px;
  }
  .HelpContainer {
    margin-right: -7px;
    width: 320px;
  }
}
/* Transitions */
.search-transition-enter-active,
.search-transition-leave-active {
  transition: transform 0.2s, opacity 0.2s;
}
.search-transition-enter,
.search-transition-leave-to {
  opacity: 0;
  transform: scaleX(0.6);
}
.help-transition-enter-active,
.help-transition-leave-active {
  transform-origin: top;
  transition: transform 0.2s, opacity 0.2s;
}
.help-transition-enter,
.help-transition-leave-to {
  opacity: 0;
  transform: scaleY(0.8);
}
</style>
<style>
.js-focus-visible .LmSearchInput.focus-visible {
  outline: none !important;
  border: none !important;
}
</style>
